/**
 * User Management API Service
 * Provides methods for managing users in O365 tenants via Microsoft Graph API.
 *
 * Updated for Phase 4 MSAL Migration - uses Bearer token authentication.
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { ApiService } from './api';
import type {
  TenantUser,
  TenantUserDetails,
  UserCreateRequest,
  UserUpdateRequest,
  PasswordResetResult,
  LicenseInfo,
  LicenseAssignmentRequest,
  PaginatedUsers,
  BulkUserResult,
  BulkLicenseRequest,
  BulkLicenseResult,
} from '../types/user';
import type { BatchAuthMethodsResponse } from '../types/authenticationMethods';

interface UsersCacheEntry {
  data: PaginatedUsers;
  timestamp: number;
}

// Module-level cache to persist across component mounts
const usersCache = new Map<string, UsersCacheEntry>();
const USERS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * User Management API Client
 */
class UserApiServiceClass {
  /**
   * Get base URL for user endpoints.
   */
  private getBaseUrl(): string {
    return '/api/users';
  }

  /**
   * List users in tenant with pagination and optional search.
   *
   * @param tenantId - Azure AD tenant ID (optional if organizationId provided)
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of users per page (default: 50)
   * @param searchQuery - Optional search term for filtering users
   * @param sync - Trigger delta sync with Azure AD before listing (default: false).
   *               Set to true when tenant changes or manual refresh clicked.
   *               Set to false for pagination or search query changes.
   *               When true, bypasses cache and fetches fresh data.
   * @param organizationId - Optional organization ID to filter users.
   *                         When provided without tenantId, backend auto-resolves to home tenant.
   * @returns Paginated list of users from local database
   */
  async listUsers(
    tenantId: string | undefined,
    page: number = 1,
    pageSize: number = 50,
    searchQuery?: string,
    sync: boolean = false,
    organizationId?: number
  ): Promise<PaginatedUsers> {
    // Create cache key from all parameters
    const cacheKey = `${tenantId ?? 'none'}:${page}:${pageSize}:${searchQuery ?? ''}:${organizationId ?? 'none'}`;

    // Check cache first (unless sync=true for manual refresh or tenant change)
    if (!sync) {
      const cached = usersCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < USERS_CACHE_TTL_MS) {
        return cached.data;
      }
    }

    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sync: sync.toString(),
    });

    // Only include tenant_id if provided.
    // When organizationId is given without tenantId, the backend auto-resolves:
    // - home_tenant auth mode: uses settings.azure_tenant_id (home tenant)
    // - federated auth mode: uses the organization's azure_tenant_id
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    if (searchQuery) {
      params.append('search_query', searchQuery);
    }

    if (organizationId !== undefined) {
      params.append('organization_id', organizationId.toString());
    }

    const result = await ApiService.request<PaginatedUsers>(
      `${this.getBaseUrl()}?${params}`,
      { method: 'GET' }
    );

    // Update cache
    usersCache.set(cacheKey, { data: result, timestamp: Date.now() });

    return result;
  }

  /**
   * Invalidate users cache for a specific tenant.
   * Call this after create, update, or delete operations.
   */
  invalidateCache(tenantId?: string): void {
    if (tenantId) {
      // Remove all cache entries for this tenant
      for (const key of usersCache.keys()) {
        if (key.startsWith(`${tenantId}:`)) {
          usersCache.delete(key);
        }
      }
    } else {
      // Clear all cache
      usersCache.clear();
    }
  }

  /**
   * Create new user in tenant.
   */
  async createUser(
    tenantId: string,
    userData: UserCreateRequest,
    organizationId?: number
  ): Promise<TenantUser> {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (organizationId !== undefined) {
      params.append('organization_id', organizationId.toString());
    }

    const result = await ApiService.request<TenantUser>(
      `${this.getBaseUrl()}?${params}`,
      {
        method: 'POST',
        body: JSON.stringify(userData),
      }
    );

    // Invalidate cache after creating user
    this.invalidateCache(tenantId);

    return result;
  }

  /**
   * Get detailed user information by ID.
   */
  async getUser(tenantId: string, userId: string): Promise<TenantUserDetails> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    return ApiService.request<TenantUserDetails>(
      `${this.getBaseUrl()}/${userId}?${params}`,
      { method: 'GET' }
    );
  }

  /**
   * Update user properties.
   *
   * Requires either tenantId or organizationId for tenant resolution.
   * - If tenantId is provided, it's used directly
   * - If organizationId is provided, the backend resolves the tenant based on
   *   the organization's authentication mode (home_tenant or federated)
   */
  async updateUser(
    userId: string,
    updates: UserUpdateRequest,
    options: { tenantId?: string; organizationId?: number }
  ): Promise<TenantUser> {
    const params = new URLSearchParams();
    if (options.tenantId) {
      params.append('tenant_id', options.tenantId);
    }
    if (options.organizationId) {
      params.append('organization_id', options.organizationId.toString());
    }

    const result = await ApiService.request<TenantUser>(
      `${this.getBaseUrl()}/${userId}?${params}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }
    );

    // Invalidate cache after updating user
    // Pass tenantId if available, otherwise clear all cache
    this.invalidateCache(options.tenantId);

    return result;
  }

  /**
   * Delete user from tenant.
   */
  async deleteUser(tenantId: string, userId: string): Promise<boolean> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    await ApiService.request<void>(
      `${this.getBaseUrl()}/${userId}?${params}`,
      { method: 'DELETE' }
    );

    // Invalidate cache after deleting user
    this.invalidateCache(tenantId);

    return true;
  }

  /**
   * Reset user password.
   */
  async resetPassword(
    tenantId: string,
    userId: string,
    requireChange: boolean = true
  ): Promise<PasswordResetResult> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    return ApiService.request<PasswordResetResult>(
      `${this.getBaseUrl()}/${userId}/reset-password?${params}`,
      {
        method: 'POST',
        body: JSON.stringify({ require_change: requireChange }),
      }
    );
  }

  /**
   * Get available licenses for tenant.
   */
  async getAvailableLicenses(tenantId: string): Promise<LicenseInfo[]> {
    return ApiService.request<LicenseInfo[]>(
      `/api/tenants/${tenantId}/licenses`,
      { method: 'GET' }
    );
  }

  /**
   * Manage user licenses (add/remove).
   */
  async manageLicenses(
    tenantId: string,
    userId: string,
    licenseRequest: LicenseAssignmentRequest
  ): Promise<TenantUser> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    return ApiService.request<TenantUser>(
      `${this.getBaseUrl()}/${userId}/licenses?${params}`,
      {
        method: 'POST',
        body: JSON.stringify(licenseRequest),
      }
    );
  }

  /**
   * Bulk create multiple users.
   */
  async bulkCreateUsers(
    tenantId: string,
    users: UserCreateRequest[]
  ): Promise<BulkUserResult> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    // Backend expects list directly, not wrapped in object
    const result = await ApiService.request<BulkUserResult>(
      `${this.getBaseUrl()}/bulk?${params}`,
      {
        method: 'POST',
        body: JSON.stringify(users),
      }
    );

    // Invalidate cache after bulk create
    this.invalidateCache(tenantId);

    return result;
  }

  /**
   * Bulk assign or remove licenses for multiple users.
   */
  async bulkAssignLicenses(
    tenantId: string,
    request: BulkLicenseRequest
  ): Promise<BulkLicenseResult> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    const result = await ApiService.request<BulkLicenseResult>(
      `${this.getBaseUrl()}/licenses/bulk?${params}`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    // Invalidate cache after bulk license changes (user data updated)
    this.invalidateCache(tenantId);

    return result;
  }

  /**
   * Get TAP (Temporary Access Pass) status for a user.
   * Returns list of TAP authentication methods configured for the user.
   */
  async getTAPStatus(tenantId: string, userId: string): Promise<Array<Record<string, unknown>>> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    return ApiService.request<Array<Record<string, unknown>>>(
      `${this.getBaseUrl()}/${userId}/tap?${params}`,
      { method: 'GET' }
    );
  }

  /**
   * Revoke (delete) a TAP authentication method for a user.
   */
  async revokeTAP(tenantId: string, userId: string, tapId: string): Promise<void> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    await ApiService.request<void>(
      `${this.getBaseUrl()}/${userId}/tap/${tapId}?${params}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Get authentication methods for multiple users in batch.
   *
   * Used by the Authentication Status tab to display all authentication methods
   * (Phone/SMS, Email, Microsoft Authenticator, FIDO2, TAP, Software OATH,
   * Hardware OATH, Windows Hello) for all users.
   *
   * @param tenantId - Azure AD tenant ID
   * @param userIds - List of user Azure AD object IDs (max 100)
   * @returns Mapping of user_id to list of authentication methods
   */
  async getBatchAuthenticationMethods(
    tenantId: string,
    userIds: string[]
  ): Promise<BatchAuthMethodsResponse> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    return ApiService.request<BatchAuthMethodsResponse>(
      `${this.getBaseUrl()}/authentication-methods/batch?${params}`,
      {
        method: 'POST',
        body: JSON.stringify({ user_ids: userIds }),
      }
    );
  }
}

export const UserApiService = new UserApiServiceClass();
